import { useCallback, useEffect, useState } from 'react';
import { Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { departmentLabelKey } from '@nbh/shared';
import type { Department, TempPasswordResponse } from '@nbh/shared';
import { adminApi, type AdminUser } from '../../lib/adminApi';
import {
  ConfirmWithReason,
  DeleteDialog,
  ROLE_LABEL_KEY,
  RoleChangeDialog,
} from './SuperAdminDialogs';
import { CreatePrivilegedUserForm, TempPasswordNotice } from './SuperAdminStaffForm';

type PendingAction = { kind: 'deactivate' | 'role' | 'delete'; user: AdminUser };

/**
 * Staff accounts at the root tier: create, move between roles, and switch access off.
 *
 * There is no delete. Deactivating leaves the account, its tickets and its history
 * exactly where they are — it only stops the person signing in.
 */
export function SuperAdminStaff({ selfId }: { selfId: string }) {
  const { t } = useTranslation();
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [credential, setCredential] = useState<TempPasswordResponse | null>(null);
  const [pending, setPending] = useState<PendingAction | null>(null);

  const load = useCallback(() => {
    adminApi.superAdmin.users
      .list()
      .then(setUsers)
      .catch((caught: Error) => setError(caught.message));
  }, []);

  useEffect(load, [load]);

  /** Errors are left to propagate: the dialog shows them next to its own confirm. */
  async function confirmed(action: () => Promise<unknown>) {
    setError(null);
    await action();
    setPending(null);
    load();
  }

  async function direct(action: () => Promise<unknown>) {
    setError(null);
    try {
      await action();
      load();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t('superAdmin.failed'));
    }
  }

  if (!users) {
    return error ? (
      <p role="alert" className="text-sm text-red-400">
        {error}
      </p>
    ) : (
      <p className="text-sm text-slate-400">{t('superAdmin.loading')}</p>
    );
  }

  return (
    <div className="space-y-3">
      <CreatePrivilegedUserForm onCreated={setCredential} onError={setError} />

      {credential && (
        <TempPasswordNotice credential={credential} onDismiss={() => setCredential(null)} />
      )}

      {error && (
        <p role="alert" className="text-sm text-red-400">
          {error}
        </p>
      )}

      {users.length === 0 ? (
        <p className="text-sm text-slate-500">{t('superAdmin.staff.empty')}</p>
      ) : (
        <ul className="divide-y divide-slate-800 rounded-xl border border-slate-800">
          {users.map((user) => (
            <StaffRow
              key={user.id}
              user={user}
              isSelf={user.id === selfId}
              onChangeRole={() => setPending({ kind: 'role', user })}
              onDeactivate={() => setPending({ kind: 'deactivate', user })}
              onReactivate={() => void direct(() => adminApi.superAdmin.users.reactivate(user.id))}
              onDelete={() => setPending({ kind: 'delete', user })}
            />
          ))}
        </ul>
      )}

      {pending?.kind === 'deactivate' && (
        <ConfirmWithReason
          subject={pending.user.name}
          headingKey="superAdmin.deactivate.heading"
          explainKey="superAdmin.deactivate.explain"
          reasonLabelKey="superAdmin.deactivate.reason"
          reasonPlaceholderKey="superAdmin.deactivate.reasonPlaceholder"
          confirmKey="superAdmin.deactivate.confirm"
          workingKey="superAdmin.deactivate.working"
          onConfirm={(reason) =>
            confirmed(() => adminApi.superAdmin.users.deactivate(pending.user.id, { reason }))
          }
          onCancel={() => setPending(null)}
        />
      )}

      {pending?.kind === 'delete' && (
        <DeleteDialog
          subject={pending.user.name}
          // The email, not the name: two people called Ana Silva is ordinary in a
          // hotel, and the value typed here must identify exactly one account.
          confirmValue={pending.user.email}
          explainKey="superAdmin.delete.explainAccount"
          onConfirm={(reason, confirm) =>
            confirmed(() => adminApi.superAdmin.users.remove(pending.user.id, { reason, confirm }))
          }
          onCancel={() => setPending(null)}
        />
      )}

      {pending?.kind === 'role' && (
        <RoleChangeDialog
          subject={pending.user.name}
          currentRole={pending.user.role}
          onConfirm={(role, reason) =>
            confirmed(() => adminApi.superAdmin.users.changeRole(pending.user.id, { role, reason }))
          }
          onCancel={() => setPending(null)}
        />
      )}
    </div>
  );
}

function StaffRow({
  user,
  isSelf,
  onChangeRole,
  onDeactivate,
  onReactivate,
  onDelete,
}: {
  user: AdminUser;
  isSelf: boolean;
  onChangeRole: () => void;
  onDeactivate: () => void;
  onReactivate: () => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation();
  const roleKey = ROLE_LABEL_KEY[user.role as keyof typeof ROLE_LABEL_KEY];

  return (
    <li className="flex flex-wrap items-center gap-2 p-3">
      {/*
        Full width on a phone, so the name and address get their own line. Three
        controls plus a name on a 390px screen truncated "Duty Manager" to "Duty Man…",
        and an operator about to delete an account has to be able to read which one.
      */}
      <span className="min-w-0 basis-full sm:flex-1 sm:basis-auto">
        <span className="flex items-center gap-2">
          <span
            className={`min-w-0 truncate text-sm ${
              user.isActive ? 'text-slate-100' : 'text-slate-500'
            }`}
          >
            {user.name}
          </span>
          {isSelf && (
            <span className="shrink-0 rounded border border-slate-700 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-slate-400">
              {t('superAdmin.staff.you')}
            </span>
          )}
          {!user.isActive && (
            <span className="shrink-0 rounded border border-amber-700/60 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-amber-300">
              {t('superAdmin.staff.inactive')}
            </span>
          )}
        </span>
        <span className="block truncate text-xs text-slate-500">
          {user.email} · {roleKey ? t(roleKey) : user.role} ·{' '}
          {t(departmentLabelKey[user.department as Department])}
        </span>
      </span>

      {/* Root cannot demote or lock out its own account from here. The server refuses
          it too; offering the button would only produce a 403 nobody can act on. */}
      {isSelf ? (
        <span className="text-xs text-slate-600">{t('superAdmin.staff.selfProtected')}</span>
      ) : (
        <>
          <button
            type="button"
            onClick={onChangeRole}
            className="min-h-[48px] rounded bg-slate-800 px-3 text-xs text-slate-200"
          >
            {t('superAdmin.staff.changeRole')}
          </button>
          <button
            type="button"
            onClick={user.isActive ? onDeactivate : onReactivate}
            className={`min-h-[48px] rounded px-3 text-xs ${
              user.isActive ? 'bg-transparent text-red-300' : 'bg-transparent text-emerald-300'
            }`}
          >
            {user.isActive ? t('superAdmin.staff.deactivate') : t('superAdmin.staff.reactivate')}
          </button>
          {/*
            Icon-only and behind a divider, last in the row. Deactivate is reversible
            and this is not; presenting them as two similar words side by side is how a
            gloved thumb picks the wrong one.
          */}
          <button
            type="button"
            onClick={onDelete}
            aria-label={t('superAdmin.staff.delete', { name: user.name })}
            title={t('superAdmin.staff.delete', { name: user.name })}
            className="min-h-[48px] border-l border-slate-800 bg-transparent px-3 text-red-500"
          >
            <Trash2 className="size-4" aria-hidden="true" />
          </button>
        </>
      )}
    </li>
  );
}
